import locationSetsApi from 'common/src/modules/locationSets/locationSets.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../campaignSetupAgent.enums';

const getLocationSetsToolSchema = z.object({
  prompt: z
    .string()
    .describe(
      'Detailed description of the location set to search for following the user prompt. If a specific location set ID is mentioned, include it in the prompt.',
    ),
});

function getLocationSetsTool() {
  async function execute({
    input: {prompt},
  }: ToolExecuteParams<typeof getLocationSetsToolSchema>): Promise<ToolResult> {
    try {
      const searchResponse = await locationSetsApi.searchLocationSets(prompt);

      if (!searchResponse.locationSets.length) {
        return toolResult(`No location sets found. Reason: ${searchResponse.filtersExplanation}`);
      }

      const locationSets = searchResponse.locationSets;

      const instructions = [
        locationSets.length > 1
          ? 'More than one location set found, use the first location set and ask the user for clarification.'
          : '',
        'When setting the location set for the card, you must always set the card feed ID to match the location set feed ID.',
      ]
        .filter(Boolean)
        .join(' ');

      return toolResult(
        JSON.stringify({
          locationSets: locationSets.map((set) => ({
            id: set.id,
            name: set.name,
            feedId: set.feedId,
          })),
          instructions,
        }),
      );
    } catch (error) {
      return toolResult(
        `Error retrieving location sets: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetLocationSets,
    displayName: 'Searching location sets',
    description: 'Search for location sets from the account library.',
    execute,
    inputSchema: getLocationSetsToolSchema,
  });
}

export default getLocationSetsTool;
